import { useEffect, useMemo, useRef, useState, useId } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { GitBranch, MousePointerClick, Link2, Eye, EyeOff, X, Star, BookOpen } from 'lucide-react';
import { persons, comiteGouvernanceTI, getPersonById, type Person, poleOrder, polesMetadata } from './orgData';
import PersonDrawer from './PersonDrawer';
import CommitteeCard from './CommitteeCard';

interface ConnectorLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  type: 'hierarchical' | 'functional';
  from: Person['id'];
  to: Person['id'];
}

export default function OrgChart() {
  const instanceId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [selectedPersonId, setSelectedPersonId] = useState<Person['id'] | null>(null);
  const [showFunctional, setShowFunctional] = useState(true);
  const [lines, setLines] = useState<ConnectorLine[]>([]);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isCommitteeOpen, setIsCommitteeOpen] = useState(false);
  const [isDanyToCorpoModalOpen, setIsDanyToCorpoModalOpen] = useState(false);
  const [isYannickToCorpoModalOpen, setIsYannickToCorpoModalOpen] = useState(false);
  const [isFrancoisToSalesModalOpen, setIsFrancoisToSalesModalOpen] = useState(false);
  const [isRichardToCorpoModalOpen, setIsRichardToCorpoModalOpen] = useState(false);

  const detailOpen = !!selectedPersonId || isGuideOpen || isCommitteeOpen || isDanyToCorpoModalOpen || isYannickToCorpoModalOpen || isFrancoisToSalesModalOpen || isRichardToCorpoModalOpen;
  // Shared by both CE surfaces: keep nested details above their parent, trap focus
  // and let Escape dismiss only the detail, not the enclosing workshop dialog.
  useEffect(() => {
    if (!detailOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    const detail = rootRef.current?.querySelector<HTMLElement>('[role="dialog"]');
    const focusable = () => Array.from(detail?.querySelectorAll<HTMLElement>('button, a[href], select, input, [tabindex="0"]') ?? []).filter(el => el.getClientRects().length > 0);
    const frame = requestAnimationFrame(() => focusable()[0]?.focus());
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopImmediatePropagation();
        setSelectedPersonId(null); setIsGuideOpen(false); setIsCommitteeOpen(false);
        setIsDanyToCorpoModalOpen(false); setIsYannickToCorpoModalOpen(false);
        setIsFrancoisToSalesModalOpen(false); setIsRichardToCorpoModalOpen(false);
      } else if (event.key === 'Tab') {
        const items = focusable(); const first = items[0]; const last = items[items.length - 1];
        if (first && (event.shiftKey ? document.activeElement === first : document.activeElement === last)) {
          event.preventDefault(); (event.shiftKey ? last : first).focus();
        }
      }
    };
    window.addEventListener('keydown', key, true);
    return () => { cancelAnimationFrame(frame); window.removeEventListener('keydown', key, true); previous?.focus(); };
  }, [detailOpen]);

  const peopleToDisplay = useMemo(() => persons, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const cards = Array.from(
      container.querySelectorAll<HTMLElement>('[data-person-id]'),
    );

    const centers = new Map<
      Person['id'],
      { x: number; y: number }
    >();

    cards.forEach((card) => {
      const id = card.dataset.personId as Person['id'] | undefined;
      if (!id) return;
      const rect = card.getBoundingClientRect();
      centers.set(id, {
        x: rect.left - containerRect.left + rect.width / 2,
        y: rect.top - containerRect.top + rect.height / 2,
      });
    });

    const danyCenter = centers.get('dany');
    const yannickCenter = centers.get('yannick');
    const francoisCenter = centers.get('francois');
    const jpCenter = centers.get('jp');
    const francisCenter = centers.get('francis');
    const melissaCenter = centers.get('melissa');
    const lahCenter = centers.get('lah');
    const antoineCenter = centers.get('antoine');
    const rosalieCenter = centers.get('rosalie');
    const richardCenter = centers.get('rdelisle');

    if (
      !showFunctional ||
      (!danyCenter && !yannickCenter && !francoisCenter && !richardCenter && !jpCenter && !francisCenter)
    ) {
      setLines([]);
      return;
    }

    const newLines: ConnectorLine[] = [];

    if (danyCenter && melissaCenter) {
      newLines.push({
        x1: danyCenter.x,
        y1: danyCenter.y,
        x2: melissaCenter.x,
        y2: melissaCenter.y,
        type: 'functional',
        from: 'dany',
        to: 'melissa',
      });
    }

    if (danyCenter && lahCenter) {
      newLines.push({
        x1: danyCenter.x,
        y1: danyCenter.y,
        x2: lahCenter.x,
        y2: lahCenter.y,
        type: 'functional',
        from: 'dany',
        to: 'lah',
      });
    }

    if (yannickCenter && melissaCenter) {
      newLines.push({
        x1: yannickCenter.x,
        y1: yannickCenter.y,
        x2: melissaCenter.x,
        y2: melissaCenter.y,
        type: 'functional',
        from: 'yannick',
        to: 'melissa',
      });
    }

    if (yannickCenter && lahCenter) {
      newLines.push({
        x1: yannickCenter.x,
        y1: yannickCenter.y,
        x2: lahCenter.x,
        y2: lahCenter.y,
        type: 'functional',
        from: 'yannick',
        to: 'lah',
      });
    }

    if (francoisCenter && antoineCenter) {
      newLines.push({
        x1: francoisCenter.x,
        y1: francoisCenter.y,
        x2: antoineCenter.x,
        y2: antoineCenter.y,
        type: 'functional',
        from: 'francois',
        to: 'antoine',
      });
    }

    if (francoisCenter && rosalieCenter) {
      newLines.push({
        x1: francoisCenter.x,
        y1: francoisCenter.y,
        x2: rosalieCenter.x,
        y2: rosalieCenter.y,
        type: 'functional',
        from: 'francois',
        to: 'rosalie',
      });
    }

    if (richardCenter && francoisCenter) {
      newLines.push({
        x1: richardCenter.x,
        y1: richardCenter.y,
        x2: francoisCenter.x,
        y2: francoisCenter.y,
        type: 'functional',
        from: 'rdelisle',
        to: 'francois',
      });
    }

    if (richardCenter && lahCenter) {
      newLines.push({
        x1: richardCenter.x,
        y1: richardCenter.y,
        x2: lahCenter.x,
        y2: lahCenter.y,
        type: 'functional',
        from: 'rdelisle',
        to: 'lah',
      });
    }

    // Ligne rouge entre les deux DÉPARTEMENTS (sections) Service TI ↔ Solutions (PRIsME)
    if (showFunctional) {
      const serviceOpsSection = container.querySelector<HTMLElement>('[data-pole-id="serviceOps"]');
      const solutionsSection = container.querySelector<HTMLElement>('[data-pole-id="solutionsPrisme"]');
      if (serviceOpsSection && solutionsSection) {
        const r1 = serviceOpsSection.getBoundingClientRect();
        const r2 = solutionsSection.getBoundingClientRect();
        newLines.push({
          x1: r1.right - containerRect.left,
          y1: r1.top - containerRect.top + r1.height / 2,
          x2: r2.left - containerRect.left,
          y2: r2.top - containerRect.top + r2.height / 2,
          type: 'functional',
          from: 'jp',
          to: 'francis',
        });
      }
    }

    setLines(newLines);
  }, [peopleToDisplay, showFunctional]);

  // Recalcul des lignes au resize (modal ouverte, scroll) pour que la ligne rouge soit bien positionnée
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => {
      const containerRect = container.getBoundingClientRect();
      const cards = Array.from(container.querySelectorAll<HTMLElement>('[data-person-id]'));
      const centers = new Map<Person['id'], { x: number; y: number }>();
      cards.forEach((card) => {
        const id = card.dataset.personId as Person['id'] | undefined;
        if (!id) return;
        const rect = card.getBoundingClientRect();
        centers.set(id, { x: rect.left - containerRect.left + rect.width / 2, y: rect.top - containerRect.top + rect.height / 2 });
      });
      const danyCenter = centers.get('dany');
      const yannickCenter = centers.get('yannick');
      const francoisCenter = centers.get('francois');
      const jpCenter = centers.get('jp');
      const francisCenter = centers.get('francis');
      const melissaCenter = centers.get('melissa');
      const lahCenter = centers.get('lah');
      const antoineCenter = centers.get('antoine');
      const rosalieCenter = centers.get('rosalie');
      const richardCenter = centers.get('rdelisle');
      if (!showFunctional || (!danyCenter && !yannickCenter && !francoisCenter && !richardCenter && !jpCenter && !francisCenter)) {
        setLines([]);
        return;
      }
      const newLines: ConnectorLine[] = [];
      if (danyCenter && melissaCenter) newLines.push({ x1: danyCenter.x, y1: danyCenter.y, x2: melissaCenter.x, y2: melissaCenter.y, type: 'functional', from: 'dany', to: 'melissa' });
      if (danyCenter && lahCenter) newLines.push({ x1: danyCenter.x, y1: danyCenter.y, x2: lahCenter.x, y2: lahCenter.y, type: 'functional', from: 'dany', to: 'lah' });
      if (yannickCenter && melissaCenter) newLines.push({ x1: yannickCenter.x, y1: yannickCenter.y, x2: melissaCenter.x, y2: melissaCenter.y, type: 'functional', from: 'yannick', to: 'melissa' });
      if (yannickCenter && lahCenter) newLines.push({ x1: yannickCenter.x, y1: yannickCenter.y, x2: lahCenter.x, y2: lahCenter.y, type: 'functional', from: 'yannick', to: 'lah' });
      if (francoisCenter && antoineCenter) newLines.push({ x1: francoisCenter.x, y1: francoisCenter.y, x2: antoineCenter.x, y2: antoineCenter.y, type: 'functional', from: 'francois', to: 'antoine' });
      if (francoisCenter && rosalieCenter) newLines.push({ x1: francoisCenter.x, y1: francoisCenter.y, x2: rosalieCenter.x, y2: rosalieCenter.y, type: 'functional', from: 'francois', to: 'rosalie' });
      if (richardCenter && francoisCenter) newLines.push({ x1: richardCenter.x, y1: richardCenter.y, x2: francoisCenter.x, y2: francoisCenter.y, type: 'functional', from: 'rdelisle', to: 'francois' });
      if (richardCenter && lahCenter) newLines.push({ x1: richardCenter.x, y1: richardCenter.y, x2: lahCenter.x, y2: lahCenter.y, type: 'functional', from: 'rdelisle', to: 'lah' });
      const serviceOpsSection = container.querySelector<HTMLElement>('[data-pole-id="serviceOps"]');
      const solutionsSection = container.querySelector<HTMLElement>('[data-pole-id="solutionsPrisme"]');
      if (serviceOpsSection && solutionsSection) {
        const r1 = serviceOpsSection.getBoundingClientRect();
        const r2 = solutionsSection.getBoundingClientRect();
        newLines.push({
          x1: r1.right - containerRect.left,
          y1: r1.top - containerRect.top + r1.height / 2,
          x2: r2.left - containerRect.left,
          y2: r2.top - containerRect.top + r2.height / 2,
          type: 'functional',
          from: 'jp',
          to: 'francis',
        });
      }
      setLines(newLines);
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [showFunctional]);

  const selectedPerson = selectedPersonId ? getPersonById(selectedPersonId) : undefined;

  return (
    <div ref={rootRef} data-org-chart className="space-y-4 min-w-0">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-indigo-300 font-semibold mb-1">
            Matrice vivante — structure, responsabilités, liens hiérarchiques &amp; fonctionnels
          </p>
          <p className="text-sm text-slate-200/90">
            Vue exécutive par défaut. Déplie en un clic pour voir les directions, les liens fonctionnels et le comité de
            gouvernance TI.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <div className="inline-flex rounded-full border border-indigo-500/40 bg-slate-900/80 px-2 py-1 items-center gap-1">
            <GitBranch className="w-3 h-3 text-indigo-300" />
            <span className="text-slate-100">Lien hiérarchique</span>
          </div>
          <div className="inline-flex rounded-full border border-emerald-500/40 bg-slate-900/80 px-2 py-1 items-center gap-1">
            <GitBranch className="w-3 h-3 text-emerald-300" strokeDasharray="4 4" />
            <span className="text-slate-100">Lien fonctionnel</span>
          </div>
          <button
            type="button"
            onClick={() => setIsGuideOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/5 hover:bg-white/10 px-3 py-1 text-slate-100 transition"
          >
            <BookOpen className="w-3.5 h-3.5 text-slate-200" />
            Guide
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 relative" ref={containerRef}>
        {/* Connecteurs */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none z-20">
          <defs>
            <marker
              id={`${instanceId}-arrow-head`}
              markerWidth="6"
              markerHeight="6"
              refX="4"
              refY="3"
              orient="auto"
            >
              <path d="M0,0 L6,3 L0,6 z" fill="currentColor" />
            </marker>
          </defs>
          {lines.map((line, index) => {
            const isCriticalTiPrisme =
              (line.from === 'jp' && line.to === 'francis') || (line.from === 'francis' && line.to === 'jp');

            const strokeColor = isCriticalTiPrisme ? 'rgb(248, 113, 113)' : 'rgb(16, 185, 129)'; // red-400 vs emerald-500
            const strokeWidth = isCriticalTiPrisme ? 4.5 : 3.2;
            const hitboxWidth = isCriticalTiPrisme ? 14 : 10;

            return (
              <g
                key={index}
                role="button"
                tabIndex={0}
                aria-label={`Lien fonctionnel ${getPersonById(line.from)?.name} vers ${getPersonById(line.to)?.name}`}
                onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.currentTarget.dispatchEvent(new MouseEvent('click', { bubbles: true })); } }}
                className="pointer-events-auto cursor-pointer"
                onClick={() => {
                  if (line.from === 'dany') {
                    setIsDanyToCorpoModalOpen(true);
                  } else if (line.from === 'yannick') {
                    setIsYannickToCorpoModalOpen(true);
                  } else if (line.from === 'francois') {
                    setIsFrancoisToSalesModalOpen(true);
                  } else if (line.from === 'rdelisle') {
                    setIsRichardToCorpoModalOpen(true);
                  } else if (line.from === 'jp' || line.to === 'jp') {
                    setIsCommitteeOpen(true);
                  }
                }}
              >
                {/* Large hitbox invisible */}
                <line
                  x1={line.x1}
                  y1={line.y1}
                  x2={line.x2}
                  y2={line.y2}
                  stroke="transparent"
                  strokeWidth={hitboxWidth}
                />
                {/* Ligne visible */}
                <line
                  x1={line.x1}
                  y1={line.y1}
                  x2={line.x2}
                  y2={line.y2}
                  stroke={strokeColor}
                  strokeWidth={strokeWidth}
                  strokeDasharray="4 4"
                  className="opacity-90"
                />
              </g>
            );
          })}
        </svg>

        {/* Colonne principale */}
        <div className="relative z-10 flex-1 space-y-4">
          {/* CE exécutif */}
          <div className="bg-slate-900/85 border border-indigo-500/50 rounded-2xl p-3 space-y-2 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[0.7rem] uppercase tracking-wide text-indigo-300 font-semibold">
                  Dany CA/CE
                </p>
                <p className="text-[0.75rem] text-slate-200/90">
                  Trois rôles complémentaires : croissance, gouvernance corporative et exécution TI.
                </p>
              </div>
              <div className="hidden md:flex items-center gap-1 text-[0.65rem] text-slate-300/80">
                <MousePointerClick className="w-3 h-3" />
                Cliquer pour ouvrir la fiche
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1.5">
              {(['dany', 'francois', 'yannick'] as Person['id'][]).map((id) => {
                const person = getPersonById(id);
                if (!person) return null;

                const initials = person.name
                  .split(' ')
                  .map((part) => part[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase();

                const isPDG = person.id === 'dany';

                return (
                  <button
                    key={person.id}
                    type="button"
                    data-person-id={person.id}
                    className={`flex items-center gap-3 rounded-2xl border px-3.5 py-2.5 text-left transition shadow-sm ${
                      selectedPersonId === person.id
                        ? 'bg-slate-950/80 border-emerald-400/80 shadow-emerald-500/40 shadow'
                        : isPDG
                        ? 'bg-emerald-500/10 border-emerald-400/80 hover:border-emerald-300/90'
                        : 'bg-slate-950/80 border-slate-700/80 hover:bg-slate-900 hover:border-indigo-400/70'
                    }`}
                    onClick={() => setSelectedPersonId(person.id)}
                  >
                    {person.avatar ? (
                      <img
                        src={person.avatar}
                        alt={person.name}
                        className="w-10 h-10 rounded-full border border-indigo-400 object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full border border-indigo-400 bg-slate-900 flex items-center justify-center text-[0.7rem] font-semibold text-slate-100">
                        {initials}
                      </div>
                    )}
                    <div className="space-y-0.5">
                      <p className="text-[0.85rem] font-semibold text-slate-50">{person.name}</p>
                      <p className="text-[0.75rem] text-slate-300/90">{person.title}</p>
                      <p className="text-[0.7rem] text-slate-400/90 line-clamp-2">{person.tagline}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Toggle liens fonctionnels seulement (vue détaillée par défaut) */}
          <div className="flex flex-wrap items-center justify-end mt-1">
            <button
              type="button"
              onClick={() => setShowFunctional((prev) => !prev)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-emerald-500/50 bg-emerald-500/10 text-xs text-emerald-100 hover:bg-emerald-500/20 transition"
            >
              {showFunctional ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              Liens fonctionnels
            </button>
          </div>

          {/* Pôles */}
          <div className="space-y-4 mt-2">
          {poleOrder.map((poleId) => {
            const meta = polesMetadata[poleId];
            const people = peopleToDisplay.filter(
              (p) =>
                p.poleId === poleId &&
                !(
                  (poleId === 'growth' && p.id === 'yannick') ||
                  (poleId === 'corporateGovernance' && p.id === 'dany')
                ),
            );
              if (people.length === 0) return null;

              return (
                <section
                  key={poleId}
                  data-pole-id={poleId}
                  className={`bg-slate-900/80 rounded-2xl border ${meta.colorClass} p-4 space-y-3`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-50 flex items-center gap-2">
                        <GitBranch className="w-4 h-4 text-indigo-300" />
                        {meta.label}
                      </h4>
                      <p className="text-xs text-slate-300/90">{meta.description}</p>
                    </div>
                      <div className="flex flex-col items-end gap-1 text-[0.65rem] text-slate-300/80">
                        <div className="hidden md:flex items-center gap-1">
                          <Link2 className="w-3 h-3" />
                          Liens hiérarchiques &amp; fonctionnels visibles
                        </div>
                        {meta.ceOwnerId && (
                          <button
                            type="button"
                            onClick={() => setSelectedPersonId(meta.ceOwnerId)}
                            className="flex items-center gap-2 bg-slate-950/80 border border-slate-700/80 rounded-full px-2.5 py-1 hover:border-emerald-400/80 hover:bg-slate-900 transition"
                          >
                            {getPersonById(meta.ceOwnerId)?.avatar && (
                              <img
                                src={getPersonById(meta.ceOwnerId)!.avatar}
                                alt={getPersonById(meta.ceOwnerId)!.name}
                                className="w-8 h-8 rounded-full border border-indigo-400 object-cover"
                              />
                            )}
                            <span className="text-[0.7rem] uppercase tracking-wide text-slate-200">
                              CE : {getPersonById(meta.ceOwnerId)?.name.split(' ')[0]}
                            </span>
                          </button>
                        )}
                      </div>
                  </div>

                  <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mt-2">
                    {people.map((person) => {
                      const initials = person.name
                        .split(' ')
                        .map((part) => part[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase();

                      const isAccountableOwner =
                        (poleId === 'growth' && person.id === 'antoine') ||
                        (poleId === 'corporateGovernance' && person.id === 'francois') ||
                        (poleId === 'serviceOps' && person.id === 'jp') ||
                        (poleId === 'solutionsPrisme' && person.id === 'francis') ||
                        (poleId === 'programmingAI' && person.id === 'edurand') ||
                        (poleId === 'cloudLicences' && person.id === 'rdelisle');

                      // Mise en évidence spéciale (fond jaune + étoile) pour certains rôles clés
                      const isHighlighted =
                        person.id === 'melissa' || person.id === 'eh' || person.id === 'jm' || person.id === 'vincent';
                      const isMelissa = person.id === 'melissa';

                      return (
                        <button
                          key={person.id}
                          type="button"
                          data-person-id={person.id}
                          className={`min-w-0 text-left rounded-xl border px-3 py-3 transition shadow-sm hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/80 ${
                            selectedPersonId === person.id
                              ? 'bg-slate-950/80 border-emerald-400/80 shadow-emerald-500/40 shadow'
                              : isHighlighted
                              ? 'bg-amber-500/10 border-amber-400/80 hover:border-amber-300/90'
                              : isAccountableOwner
                              ? 'bg-emerald-500/10 border-emerald-400/80 hover:border-emerald-300/90'
                              : 'bg-slate-950/80 border-slate-700/80 hover:border-indigo-400/70'
                          }`}
                          onClick={() => setSelectedPersonId(person.id)}
                        >
                          <div className="flex items-start gap-2 mb-1.5">
                            {person.avatar ? (
                              person.id === 'francis' ? (
                                <div className="relative w-[58px] h-8 shrink-0">
                                  <img
                                    src="/RL.png"
                                    alt="Rosemarie Labrecque"
                                    className="absolute left-0 top-0 w-8 h-8 rounded-full border border-indigo-400 object-cover"
                                    title="Rosemarie Labrecque"
                                  />
                                  <img
                                    src={person.avatar}
                                    alt={person.name}
                                    className="absolute left-[26px] top-0 w-8 h-8 rounded-full border border-indigo-400 object-cover"
                                  />
                                </div>
                              ) : (
                                <img
                                  src={person.avatar}
                                  alt={person.name}
                                  className="w-8 h-8 rounded-full border border-indigo-400 object-cover shrink-0"
                                />
                              )
                            ) : (
                              <div className="w-8 h-8 rounded-full border border-indigo-400 bg-slate-900 flex items-center justify-center text-[0.7rem] font-semibold text-slate-100 shrink-0">
                                {initials}
                              </div>
                            )}
                            <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <div className="flex items-center gap-1.5">
                                  {isHighlighted && <Star className="w-3.5 h-3.5 text-amber-300" />}
                                  <div>
                                    <p className="text-[0.78rem] font-semibold text-slate-50">{person.name}</p>
                                    <p className="text-[0.7rem] text-slate-300/90">{person.title}</p>
                                  </div>
                                </div>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-900/80 border border-slate-600/80 text-[0.6rem] uppercase tracking-wide text-slate-200">
                                  {isMelissa
                                    ? 'Spécialiste'
                                    : person.type === 'executive'
                                    ? 'Exécutif'
                                    : person.type === 'vp'
                                    ? 'VP'
                                    : person.type === 'director'
                                    ? 'Direction'
                                    : person.type === 'owner'
                                    ? 'Owner'
                                    : 'Leadership'}
                                </span>
                              </div>
                              <p className="text-[0.7rem] text-slate-200/90">{person.tagline}</p>
                              <p className="text-[0.65rem] text-slate-400">
                                <span className="font-semibold text-slate-200">Pôle :</span> {person.poleLabel}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {(poleId === 'serviceOps' || poleId === 'solutionsPrisme') && (
                    <div className="flex justify-end mt-2">
                      <button
                        type="button"
                        onClick={() => setIsCommitteeOpen(true)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-red-400/70 bg-red-500/15 text-[0.7rem] text-red-100 hover:bg-red-500/25 transition"
                      >
                        <GitBranch className="w-3 h-3 text-red-200" />
                        Comité Gouvernance TI
                      </button>
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </div>

      </div>

      {/* Fiche individu */}
      <AnimatePresence>
        {selectedPerson && (
          <PersonDrawer
            person={selectedPerson}
            onClose={() => setSelectedPersonId(null)}
          />
        )}
      </AnimatePresence>

      {/* Guide — comment lire l'organigramme */}
      <AnimatePresence>
        {isGuideOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/70 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsGuideOpen(false)}
            />
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ type: 'spring', stiffness: 260, damping: 32 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Guide organigramme"
            >
              <div className="w-[90vw] max-w-3xl max-h-[90vh] bg-slate-950 border border-indigo-500/40 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
                <div className="flex items-center justify-between px-5 py-3 border-b border-indigo-500/30 bg-slate-950/90">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/15 flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-slate-200" />
                    </div>
                    <div>
                      <p className="text-[0.7rem] uppercase tracking-wide text-indigo-300 font-semibold">
                        Guide de lecture
                      </p>
                      <h3 className="text-sm md:text-base font-semibold text-white">
                        Comment fonctionne l’organigramme
                      </h3>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsGuideOpen(false)}
                    className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/20 text-white transition"
                    aria-label="Fermer le guide"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto text-sm text-slate-100">
                  <div className="space-y-2">
                    <p className="text-[0.7rem] uppercase tracking-wide text-indigo-300 font-semibold">
                      1) Organigramme hiérarchique (la base)
                    </p>
                    <p className="text-slate-200">
                      La structure principale est <span className="font-semibold">hiérarchique et simple</span> : elle
                      montre qui porte la responsabilité d’un pôle et comment les rôles se regroupent par département.
                      Cette lecture sert à garder la gouvernance cohérente et prévisible.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">
                      2) Liens fonctionnels (tunnel “VPN” dans l’organigramme)
                    </p>
                    <p className="text-slate-200">
                      Les liens fonctionnels représentent un <span className="font-semibold">accès privilégié</span> entre
                      deux rôles/pôles pour accélérer la collaboration sans casser l’organigramme hiérarchique.
                      Imagine un <span className="font-semibold">tunnel VPN</span> à travers “l’internet” interne :
                      <span className="font-semibold"> filtré, restreint et scoped</span> au strict nécessaire (comme un
                      firewall) pour éviter les incohérences et les contournements.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <p className="text-[0.7rem] uppercase tracking-wide text-amber-300 font-semibold">
                      3) Étoiles jaunes (rôle haut niveau dans le département)
                    </p>
                    <p className="text-slate-200">
                      Une étoile jaune indique un rôle <span className="font-semibold">haut niveau</span> au sein du
                      département : même cause, même équipe, mais responsabilités critiques. Dans la gouvernance CE,
                      certains de ces rôles sont sous la <span className="font-semibold">gouverne d’un membre du CE</span>
                      (sans changer leur appartenance au département).
                    </p>
                  </div>

                  <div className="rounded-xl border border-slate-700/70 bg-slate-900/60 p-4 text-[0.85rem] text-slate-200">
                    <p className="font-semibold text-slate-100 mb-1">Interaction</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Cliquer sur une personne ouvre sa fiche (mandat, responsabilités, KPIs, garde-fous).</li>
                      <li>Cliquer sur certains liens ouvre une explication de gouvernance (pourquoi le lien existe).</li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Modal lien fonctionnel Dany → Gouvernance corporative */}
      <AnimatePresence>
        {isDanyToCorpoModalOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/70 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsDanyToCorpoModalOpen(false)}
            />
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ type: 'spring', stiffness: 260, damping: 32 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Lien direct Dany → Gouvernance corporative"
            >
              <div className="w-[90vw] max-w-4xl max-h-[90vh] bg-slate-950 border border-emerald-500/50 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
                <div className="flex items-center justify-between px-5 py-3 border-b border-emerald-500/40 bg-slate-950/90">
                  <div>
                    <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">
                      Lien fonctionnel exceptionnel
                    </p>
                    <h3 className="text-sm md:text-base font-semibold text-white">
                      Dany → Gouvernance corporative (Mélissa P. &amp; LAH)
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsDanyToCorpoModalOpen(false)}
                    className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/20 text-white transition"
                    aria-label="Fermer la modal Dany vers Gouvernance corporative"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto text-sm text-slate-100">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">
                        Esprit du lien
                      </p>
                      <p className="text-sm text-slate-200">
                        Dany a un accès direct à Mélissa (comptabilité / fiscalité) et à LAH (opérations admin / RH) pour des dossiers stratégiques, principalement de niveau CA ou de trésorerie
                        critique. Ce lien sert à accélérer la préparation d&apos;informations sensibles, jamais à
                        court-circuiter la gouvernance de François.
                      </p>
                      <p className="text-sm text-slate-200">
                        Mélissa est le pont naturel avec les professionnels externes (CPA, fiscalistes, avocats) et
                        gère aussi des entités adjacentes (ex. holding, sociétés liées). LAH est le point de
                        vérité sur la opérations administratives, les politiques RH et la réalité terrain des processus internes.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">
                        Règles de bon usage
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-200">
                        <li>
                          Utiliser ce lien surtout pour des demandes
                          <span className="font-semibold"> ponctuelles et documentées</span> (préparation d&apos;États
                          financiers, dossiers CA, décisions de financement).
                        </li>
                        <li>
                          Toujours
                          <span className="font-semibold"> informer François</span> a posteriori quand une demande
                          sort du cadre habituel ou touche la structure de contrôle interne.
                        </li>
                        <li>
                          Distinguer clairement ce qui relève du
                          <span className="font-semibold"> rôle de PDG</span> (vision, arbitrage, exigence de
                          transparence) de ce qui demeure sous la
                          <span className="font-semibold"> responsabilité opérationnelle de François</span>.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">
                        Ce que Dany peut faire
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-200">
                        <li>
                          Demander directement à Mélissa des
                          <span className="font-semibold"> états financiers, analyses ou documents</span> requis pour
                          le CA ou des décisions stratégiques.
                        </li>
                        <li>
                          Solliciter LAH pour
                          <span className="font-semibold"> clarifier une situation de revenus, marges ou opérations administratives</span>
                          quand un enjeu client ou stratégique l&apos;exige.
                        </li>
                        <li>
                          Proposer des
                          <span className="font-semibold"> pistes d&apos;amélioration technologiques / IA</span> sur les
                          outils financiers et administratifs, en mode C (consulté).
                        </li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-rose-300 font-semibold">
                        Ce que Dany ne doit pas faire
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-200">
                        <li>
                          <span className="font-semibold">Ne pas prioriser</span> le travail quotidien de Mélissa ou
                          de LAH (gestion des urgences, ordre des dossiers) à la place de François.
                        </li>
                        <li>
                          <span className="font-semibold">Ne pas imposer</span> de nouvelles méthodes, outils ou
                          processus financiers directement à l&apos;équipe sans passage par la gouvernance corporative.
                        </li>
                        <li>
                          <span className="font-semibold">Ne pas utiliser</span> ce lien pour régler un irritant
                          opérationnel mineur ou contourner une décision de François.
                        </li>
                        <li>
                          <span className="font-semibold">Ne pas demander</span> d&apos;accès ou d&apos;extractions de
                          données sensibles hors des règles habituelles (sécurité, conformité, confidentialité).
                        </li>
                        <li>
                          <span className="font-semibold">Ne pas créer de double canal</span> de gestion RH ou
                          performance avec Mélissa ou LAH : ces sujets restent sous la responsabilité de François.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-2 rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-3 text-[0.8rem] text-emerald-100">
                    <p className="font-semibold mb-1">Principe de gouvernance</p>
                    <p>
                      Ce lien fonctionnel existe pour
                      <span className="font-semibold"> renforcer la transparence et la qualité des décisions</span>, pas
                      pour affaiblir la ligne hiérarchique. François demeure A sur la gouvernance corporative; Dany agit
                      comme PDG qui demande de la clarté et propose des améliorations, sans se substituer au rôle
                      opérationnel de VP.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Modal lien fonctionnel Dany → Gouvernance corporative */}
      <AnimatePresence>
        {isYannickToCorpoModalOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/70 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsYannickToCorpoModalOpen(false)}
            />
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ type: 'spring', stiffness: 260, damping: 32 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Lien direct Dany → Gouvernance corporative"
            >
              <div className="w-[90vw] max-w-4xl max-h-[90vh] bg-slate-950 border border-emerald-500/50 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
                <div className="flex items-center justify-between px-5 py-3 border-b border-emerald-500/40 bg-slate-950/90">
                  <div>
                    <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">
                      Lien fonctionnel exceptionnel
                    </p>
                    <h3 className="text-sm md:text-base font-semibold text-white">
                      Dany → Gouvernance corporative (Mélissa P. &amp; LAH)
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsYannickToCorpoModalOpen(false)}
                    className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/20 text-white transition"
                    aria-label="Fermer la modal Dany vers Gouvernance corporative"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto text-sm text-slate-100">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">
                        Esprit du lien
                      </p>
                      <p className="text-sm text-slate-200">
                        Dany a un accès direct à Mélissa et à LAH pour comprendre en profondeur la santé des
                        clients : comptes en souffrance, marges, comportements de paiement et signaux de risque. Ce
                        lien sert à nourrir la stratégie de développement et la qualité de la relation client, pas à
                        piloter l&apos;équipe Finance / Admin à la place de François.
                      </p>
                      <p className="text-sm text-slate-200">
                        Plus Dany voit clairement la réalité financière et opérationnelle des comptes, plus il peut
                        ajuster le portefeuille, les offres et les plans de redressement. Ce canal existe donc pour
                        relier directement la
                        <span className="font-semibold"> réalité client</span> et la
                        <span className="font-semibold"> stratégie de croissance</span>.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">
                        Règles de bon usage
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-200">
                        <li>
                          Utiliser ce lien pour
                          <span className="font-semibold"> éclairer une décision client</span> (négociation, maintien
                          ou sortie de relation, plan de redressement), pas pour gérer le quotidien administratif.
                        </li>
                        <li>
                          Formuler des demandes
                          <span className="font-semibold"> claires et complètes</span> (client, période, enjeux,
                          échéance souhaitée) et valider avec François quand l&apos;échéance est serrée.
                        </li>
                        <li>
                          Accepter que
                          <span className="font-semibold"> les priorités quotidiennes restent gérées par François</span>
                          , même pour un membre du CE.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">
                        Ce que Dany peut faire
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-200">
                        <li>
                          Demander à LAH un
                          <span className="font-semibold"> portrait des comptes en souffrance</span> sur un client ou un
                          segment donné, pour préparer une stratégie de relation ou de recouvrement.
                        </li>
                        <li>
                          Demander à Mélissa des
                          <span className="font-semibold"> indicateurs financiers ciblés</span> (marge, ancienneté des
                          soldes, exposition) pour appuyer une décision d&apos;offre, de rabais ou de sortie.
                        </li>
                        <li>
                          Proposer des
                          <span className="font-semibold"> ajustements d&apos;offre ou de portefeuille</span> basés sur
                          ces informations, en coordination avec François.
                        </li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-rose-300 font-semibold">
                        Ce que Dany ne doit pas faire
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-200">
                        <li>
                          <span className="font-semibold">Ne pas traiter Mélissa ou LAH</span> comme des adjoints opérationnels
                          personnelles pour ses suivis ou ses dossiers.
                        </li>
                        <li>
                          <span className="font-semibold">Ne pas changer</span> l&apos;ordre des priorités de leur
                          journée sans validation explicite de François.
                        </li>
                        <li>
                          <span className="font-semibold">Ne pas multiplier</span> les urgences auto-déclarées : toute
                          échéance très serrée doit être
                          <span className="font-semibold"> alignée avec François</span> pour éviter les malentendus.
                        </li>
                        <li>
                          <span className="font-semibold">Ne pas court-circuiter</span> les cadres ou canaux officiels
                          (comités, processus, outils) pour &quot;aller plus vite&quot; : les mêmes règles s&apos;appliquent
                          au CE qu&apos;au reste de l&apos;organisation.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-2 rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-3 text-[0.8rem] text-emerald-100">
                    <p className="font-semibold mb-1">Principe de gouvernance</p>
                    <p>
                      Ce lien fonctionnel vise à
                      <span className="font-semibold"> rapprocher ventes, finances et relation client</span>, tout en
                      respectant strictement les
                      <span className="font-semibold"> cadres et canaux mis en place</span>. Comme pour Dany, le fait
                      d&apos;être au CE ne donne pas le droit de contourner les processus : il donne la responsabilité
                      de les faire vivre et de les respecter, en donnant l&apos;exemple.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Modal lien fonctionnel François → Ventes & Marketing */}
      <AnimatePresence>
        {isFrancoisToSalesModalOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/70 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsFrancoisToSalesModalOpen(false)}
            />
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ type: 'spring', stiffness: 260, damping: 32 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Lien direct François → Ventes & Marketing"
            >
              <div className="w-[90vw] max-w-4xl max-h-[90vh] bg-slate-950 border border-emerald-500/50 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
                <div className="flex items-center justify-between px-5 py-3 border-b border-emerald-500/40 bg-slate-950/90">
                  <div>
                    <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">
                      Lien fonctionnel équilibre Ventes ↔ Gouvernance
                    </p>
                    <h3 className="text-sm md:text-base font-semibold text-white">
                      François → Ventes &amp; Marketing (Antoine G. &amp; Rosalie E.)
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsFrancoisToSalesModalOpen(false)}
                    className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/20 text-white transition"
                    aria-label="Fermer la modal François vers Ventes & Marketing"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto text-sm text-slate-100">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">
                        Esprit du lien
                      </p>
                      <p className="text-sm text-slate-200">
                        François joue le rôle de balancier entre
                        <span className="font-semibold"> ambition commerciale</span> et
                        <span className="font-semibold"> discipline financière / conformité</span>. Ce lien direct vers
                        Antoine et Rosalie existe pour que les décisions de Ventes &amp; Marketing tiennent compte
                        immédiatement des contraintes de trésorerie, de risque et de conformité.
                      </p>
                      <p className="text-sm text-slate-200">
                        L&apos;objectif est d&apos;éviter à la fois les offres &quot;qui ne passent pas en finance&quot;
                        et les cadres financiers qui étouffent la croissance. François doit rendre
                        <span className="font-semibold"> lisibles et compréhensibles</span> les règles de jeu côté
                        gouvernance pour que l&apos;équipe ventes puisse jouer à l&apos;intérieur sans zone grise.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">
                        Règles de bon usage
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-200">
                        <li>
                          Utiliser ce lien pour
                          <span className="font-semibold"> clarifier l&apos;impact financier / risque</span> d&apos;une
                          offre, d&apos;un modèle de rabais ou d&apos;une condition contractuelle spéciale.
                        </li>
                        <li>
                          Travailler avec Antoine pour
                          <span className="font-semibold"> co-construire les procédures</span> qui touchent les ventes
                          (crédit, opérations administratives, conditions de paiement), plutôt que les imposer unilatéralement.
                        </li>
                        <li>
                          S&apos;assurer que toute nouvelle procédure qui touche le
                          <span className="font-semibold"> positionnement ou la stratégie de marché</span> soit
                          discutée avec Dany avant d&apos;être rendue officielle.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">
                        Ce que François peut faire
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-200">
                        <li>
                          Valider avec Antoine qu&apos;une
                          <span className="font-semibold"> nouvelle procédure</span> (ex.: gestion des opérations administratives,
                          crédit, pénalités) est
                          <span className="font-semibold"> réaliste sur le terrain</span> et claire pour l&apos;équipe
                          de vente.
                        </li>
                        <li>
                          Expliquer en langage simple les
                          <span className="font-semibold"> contraintes bancaires, fiscales ou de risque</span> qui
                          justifient certains garde-fous sur les offres.
                        </li>
                        <li>
                          Travailler avec Rosalie pour
                          <span className="font-semibold"> rendre visibles et compréhensibles</span> les règles de jeu
                          (procédures, matrices, guides) dans les bons outils et canaux.
                        </li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-rose-300 font-semibold">
                        Ce que François ne doit pas faire
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-200">
                        <li>
                          <span className="font-semibold">Ne pas bloquer une initiative de vente</span> directement avec
                          un représentant ou un directeur de compte sans passer par Antoine et, au besoin, Dany.
                        </li>
                        <li>
                          <span className="font-semibold">Ne pas multiplier</span> les procédures et contrôles sans se
                          soucier de leur
                          <span className="font-semibold"> pédagogie et de leur communication</span> auprès des ventes.
                        </li>
                        <li>
                          <span className="font-semibold">Ne pas utiliser</span> la conformité comme argument pour
                          enterrer une décision stratégique validée par le CE.
                        </li>
                        <li>
                          <span className="font-semibold">Ne pas contourner</span> Dany sur des décisions qui
                          modifient le
                          <span className="font-semibold"> positionnement ou la stratégie commerciale</span> de BZ.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-2 rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-3 text-[0.8rem] text-emerald-100">
                    <p className="font-semibold mb-1">Principe de gouvernance</p>
                    <p>
                      Ce lien est là pour
                      <span className="font-semibold"> aligner les chiffres et le terrain</span>, pas pour créer une
                      nouvelle couche de friction. François est A sur la conformité et les cadres; Antoine et Dany
                      sont responsables de la stratégie et de l&apos;exécution commerciale. Les décisions solides
                      naissent quand les trois réalités
                      <span className="font-semibold"> se parlent dans le bon canal</span>, avec une communication claire
                      et documentée.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Modal lien fonctionnel Richard → Gouvernance corporative */}
      <AnimatePresence>
        {isRichardToCorpoModalOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/70 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsRichardToCorpoModalOpen(false)}
            />
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ type: 'spring', stiffness: 260, damping: 32 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Lien direct Richard → Gouvernance corporative"
            >
              <div className="w-[90vw] max-w-4xl max-h-[90vh] bg-slate-950 border border-emerald-500/50 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
                <div className="flex items-center justify-between px-5 py-3 border-b border-emerald-500/40 bg-slate-950/90">
                  <div>
                    <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">
                      Lien fonctionnel contrats &amp; licences
                    </p>
                    <h3 className="text-sm md:text-base font-semibold text-white">
                      Richard → Gouvernance corporative (François D. &amp; LAH)
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsRichardToCorpoModalOpen(false)}
                    className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/20 text-white transition"
                    aria-label="Fermer la modal Richard vers Gouvernance corporative"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto text-sm text-slate-100">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">
                        Esprit du lien
                      </p>
                      <p className="text-sm text-slate-200">
                        Richard porte les
                        <span className="font-semibold"> contrats et licences</span> : chaque décision prise côté
                        Cloud / licences a un impact direct sur les flux administratifs, les opérations administratives et la conformité
                        contractuelle. Ce lien direct avec François et LAH existe pour que ces décisions soient
                        cohérentes avec la réalité financière et opérationnelle, sans créer de zones grises.
                      </p>
                      <p className="text-sm text-slate-200">
                        LAH et son équipe rendent les opérations licences
                        <span className="font-semibold"> vivantes au quotidien</span> (activation, facturation,
                        recouvrement). François s&apos;assure que les cadres et contrôles restent solides. Richard doit
                        pouvoir s&apos;appuyer sur les deux pour faire évoluer les contrats et les pratiques sans
                        fragiliser BZ.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">
                        Règles de bon usage
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-200">
                        <li>
                          Travailler avec LAH pour valider que
                          <span className="font-semibold"> chaque changement de contrat / licence</span> est
                          <span className="font-semibold"> exécutable et clair</span> pour son équipe.
                        </li>
                        <li>
                          Impliquer François quand un ajustement touche les
                          <span className="font-semibold"> risques, la trésorerie ou la conformité</span> (ex. nouvelles
                          clauses, seuils de crédit, pratiques de facturation).
                        </li>
                        <li>
                          Utiliser ce lien pour
                          <span className="font-semibold"> bâtir et améliorer les cadres</span> (gouvernance contrats,
                          procédures, matrices), pas pour gérer les urgences quotidiennes à la place de LAH.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">
                        Ce que Richard peut faire
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-200">
                        <li>
                          Discuter avec François des
                          <span className="font-semibold"> modèles de contrats et de licences</span> (ex.: types
                          d&apos;engagement, niveaux de marge, risques acceptables) avant de les généraliser.
                        </li>
                        <li>
                          Valider avec LAH comment
                          <span className="font-semibold"> une nouvelle pratique</span> (ex.: ajustement de cycle de
                          facturation, gestion d&apos;orphelins) sera gérée dans les outils et par l&apos;équipe.
                        </li>
                        <li>
                          Remonter les
                          <span className="font-semibold"> irritants terrain</span> (ex.: règles peu claires, cas
                          récurrents) pour nourrir l&apos;amélioration continue des procédures.
                        </li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-rose-300 font-semibold">
                        Ce que Richard ne doit pas faire
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-200">
                        <li>
                          <span className="font-semibold">Ne pas redéfinir</span> seul des règles financières ou de
                          crédit sans passer par François.
                        </li>
                        <li>
                          <span className="font-semibold">Ne pas surcharger</span> l&apos;équipe de LAH avec des
                          urgences répétées liées à des décisions prises sans alignement préalable.
                        </li>
                        <li>
                          <span className="font-semibold">Ne pas bypasser</span> les canaux officiels (comités,
                          procédures) pour régler un cas à court terme au détriment de la cohérence globale.
                        </li>
                        <li>
                          <span className="font-semibold">Ne pas traiter</span> ce lien comme un raccourci hiérarchique :
                          LAH reste responsable de ses priorités; François reste A sur la gouvernance corporative.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-2 rounded-xl border border-amber-400/60 bg-amber-500/10 p-3 text-[0.8rem] text-amber-100">
                    <p className="font-semibold mb-1">Lien spécifique Cloud / licences</p>
                    <p>
                      Sur le volet <span className="font-semibold">Cloud &amp; licences</span>, François agit comme
                      véritable <span className="font-semibold">spécialiste et responsable de dossier</span> du côté
                      gouvernance pour le département de Richard. Les décisions structurantes (modèles d&apos;engagement,
                      exposition au risque, mécanismes de crédit) se prennent dans ce duo, puis sont déployées avec
                      l&apos;équipe de LAH. Ce lien formel permet de tenir ensemble la
                      <span className="font-semibold"> vision Cloud de BZ, la rentabilité des contrats et la capacité
                      réelle des opérations</span>.
                    </p>
                  </div>

                  <div className="mt-2 rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-3 text-[0.8rem] text-emerald-100">
                    <p className="font-semibold mb-1">Principe de gouvernance</p>
                    <p>
                      Ce lien formalise le
                      <span className="font-semibold"> triangle Contrats / Finance / Opérations</span>. Richard porte la
                      structure des offres et des licences, François protège la cohérence financière et la conformité,
                      LAH fait vivre les règles au quotidien. Quand les trois se parlent dans ce cadre, BZ protège
                      ses marges tout en livrant une expérience client claire et prévisible.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Comité Gouvernance TI – lien Service TI ↔ Solutions d'affaires & Cybersécurité (PRIsME) */}
      <AnimatePresence>
        {isCommitteeOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/70 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsCommitteeOpen(false)}
            />
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ type: 'spring', stiffness: 260, damping: 32 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Comité Gouvernance TI"
            >
              <div className="w-[90vw] max-w-4xl max-h-[90vh] bg-slate-950 border border-red-500/60 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
                <div className="flex items-center justify-between px-5 py-3 border-b border-red-500/40 bg-slate-950/90">
                  <div>
                    <p className="text-[0.7rem] uppercase tracking-wide text-red-300 font-semibold">
                      Lien critique Service TI ↔ Solutions (PRIsME)
                    </p>
                    <h3 className="text-sm md:text-base font-semibold text-white">Comité Gouvernance TI</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsCommitteeOpen(false)}
                    className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/20 text-white transition"
                    aria-label="Fermer la modal Comité Gouvernance TI"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-4 space-y-4 overflow-y-auto text-sm text-slate-100">
                  <CommitteeCard committee={comiteGouvernanceTI} />

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-red-300 font-semibold">
                        Esprit du lien Service TI ↔ Solutions (PRIsME)
                      </p>
                      <p className="text-sm text-slate-200">
                        Le Service TI porte l&apos;exécution quotidienne (incidents, SLA, opérations récurrentes) alors
                        que Solutions d&apos;affaires &amp; Cybersécurité (PRIsME) porte l&apos;architecture, la
                        cybersécurité et la standardisation des solutions. Ce comité existe pour éviter que les deux
                        réalités tirent dans des directions opposées.
                      </p>
                      <p className="text-sm text-slate-200">
                        Pendant les prochaines années, le lien doit rester
                        <span className="font-semibold"> très serré</span> : une partie de ce que fait Solutions était
                        historiquement portée par le Service TI. La réussite du nouveau département passe par une
                        <span className="font-semibold"> communication constante</span>, une
                        <span className="font-semibold"> clarification des frontières</span> et une adoption réelle de
                        PRIsME comme
                        <span className="font-semibold"> outil standard de pilotage</span>.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-red-300 font-semibold">
                        RACI du comité
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-200">
                        <li>
                          <span className="font-semibold">A – Dany</span> : garantit que le comité aligne exécution,
                          architecture et PRIsME, et tranche les arbitrages majeurs.
                        </li>
                        <li>
                          <span className="font-semibold">R – Jean-Pierre (Service TI)</span> : pilote les chiffres,
                          les SLA et le partage de revenus associés aux services TI.
                        </li>
                        <li>
                          <span className="font-semibold">R – Francis (Solutions / PRIsME)</span> : responsable du
                          rendement, de la qualité des solutions et de l&apos;intégration dans PRIsME.
                        </li>
                        <li>
                          <span className="font-semibold">C – Dany &amp; François</span> : consultés pour la cohérence
                          marché / croissance et pour la cohérence gouvernance / risques.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-emerald-300 font-semibold">
                        Ce que le comité doit faire
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-200">
                        <li>
                          Clarifier et documenter la
                          <span className="font-semibold"> frontière</span> entre ce qui appartient au Service TI et ce
                          qui appartient à Solutions (PRIsME), et l&apos;incarner dans PRIsME (arbres / branches /
                          feuilles).
                        </li>
                        <li>
                          Prioriser les
                          <span className="font-semibold"> irritants récurrents</span> entre les deux équipes et décider
                          s&apos;ils se règlent par un changement de
                          <span className="font-semibold"> processus, d&apos;outil ou de gouvernance</span>.
                        </li>
                        <li>
                          S&apos;assurer que les
                          <span className="font-semibold"> revenus et coûts partagés</span> entre les deux départements
                          sont pilotés clairement par Jean-Pierre, avec une lecture simple pour Dany.
                        </li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[0.7rem] uppercase tracking-wide text-rose-300 font-semibold">
                        Ce que le comité ne doit pas faire
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-200">
                        <li>
                          <span className="font-semibold">Ne pas devenir</span> une troisième ligne de gestion des
                          tickets : les décisions doivent rester structurantes, pas micro-opérationnelles.
                        </li>
                        <li>
                          <span className="font-semibold">Ne pas laisser</span> les tensions entre projets / incidents
                          s&apos;envenimer sans les remonter : si l&apos;équilibre ne se trouve pas dans le comité, il
                          faut
                          <span className="font-semibold"> escalader à Dany</span> rapidement.
                        </li>
                        <li>
                          <span className="font-semibold">Ne pas créer</span> de règles parallèles non documentées dans
                          PRIsME : toute nouvelle façon de faire doit être
                          <span className="font-semibold"> reflétée dans PRIsME</span> pour l&apos;ensemble de BZ.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-2 rounded-xl border border-red-400/60 bg-red-500/10 p-3 text-[0.8rem] text-red-100">
                    <p className="font-semibold mb-1">Principe de gouvernance</p>
                    <p>
                      Ce lien Service TI ↔ Solutions (PRIsME) est
                      <span className="font-semibold"> critique</span> : il conditionne l&apos;adoption réelle de PRIsME,
                      la qualité des livrables et la clarté pour les clients. Le comité est le
                      <span className="font-semibold"> canal officiel</span> pour régler les frictions entre les deux
                      départements; si l&apos;équilibre n&apos;est pas trouvé, l&apos;escalade naturelle est vers Dany
                      comme arbitre final.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

